import { MDBTable, MDBTableBody, MDBTableHead } from "mdbreact";
import moment from "moment";
import { formatMoney } from "../../../../../../helpers/formatter";
import { Button } from "antd";
import React, { Dispatch, SetStateAction, useState } from "react";
import backBtn from "../../../../../../assets/svg/icons/back-button-black.svg";
import searchIcon from "../../../../../../assets/svg/icons/search-icon.svg";
import arrowDown from "../../../../../../assets/svg/icons/arrow-down-black.svg";
import {
  IParams,
  useFetchNIPTransactions,
} from "../../../../../../custom-hooks/useFetchNIPTransactions";
import styles from "./TransactionSetting.module.scss";
import TransactionsFilter from "./modals/TransactionsFilter";
import Paginator from "../../../../../Pagination/Pagination.component";
import { Loader } from "../../../../../../UI/Loaders/Loaders";

interface BankTransferTransaction {
  amount: number;
  beneficiaryAccountNumber: string;
  beneficiaryName: string;
  confirmationStatus: {
    name: string;
    id: number;
  };
  destinationBankCode: string;
  destinationBankName: string;
  feeAmount: number;
  originatorAccountNumber: string;
  originatorName: string;
  partnerCode: string;
  paymentReference: string;
  provider: {
    name: string;
    id: number;
  };
  sessionId: string;
  transactionDate: string; // ISO date string
  transactionStatus: {
    name: string;
    id: number;
  };
  vatAmount: number;
}

interface ITransactions {
  setShowTransactions: Dispatch<SetStateAction<boolean>>;
}

const TransactionsTable = ({ setShowTransactions }: ITransactions) => {
  const [query, setQuery] = useState<IParams>({
    page: 1,
    pageSize: 20,
    transactionStatus: "",
    startDate: "",
    endDate: "",
    sessionId: "",
    transferProvider: "",
  });
  const [showFilter, setShowFilter] = useState(false);

  const { data: transactionData, isLoading: isLoadingTransactionData } =
    useFetchNIPTransactions({
      page: query.page,
      pageSize: query.pageSize,
      transactionStatus: query.transactionStatus,
      startDate: query.startDate,
      endDate: query.endDate,
      sessionId: query.sessionId,
      transferProvider: query.transferProvider,
    });

  const handleGoBack = () => {
    setShowTransactions(false);
  };

  let RenderedComponent = null;
  if (isLoadingTransactionData) {
    RenderedComponent = (
      <div>
        <Loader centered={true} text="Loading transactions..." />
      </div>
    );
  } else if (transactionData?.length === 0) {
    RenderedComponent = <p>No transaction record</p>;
  } else {
    RenderedComponent = (
      <>
        <MDBTable
          responsive
          minimal="true"
          striped
          bordered
          hover
          id="table-to-xls"
        >
          <MDBTableHead>
            <tr>
              <th>Session ID</th>
              <th>Recipient</th>
              <th>Provider</th>
              <th>Amount</th>
              <th>Date Created</th>
              <th>Status</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {transactionData?.data?.map(
              (transaction: BankTransferTransaction) => (
                <tr key={transaction.sessionId}>
                  <td>{transaction?.sessionId}</td>
                  <td>{transaction?.beneficiaryName}</td>
                  <td>{transaction?.provider?.name}</td>
                  <td>{formatMoney(transaction?.amount)}</td>
                  <td>
                    {transaction?.transactionDate
                      ? moment(transaction?.transactionDate).format(
                          "DD-MMM-YYYY, h:mm a",
                        )
                      : "Nil"}
                  </td>
                  <td>
                    <span
                      className={
                        transaction?.transactionStatus?.name?.toLowerCase() ===
                        "processing"
                          ? styles.processing
                          : transaction?.transactionStatus?.name?.toLowerCase() ===
                              "success"
                            ? styles.success
                            : styles.failed
                      }
                    >
                      {transaction?.transactionStatus?.name}
                    </span>
                  </td>
                </tr>
              ),
            )}
          </MDBTableBody>
        </MDBTable>
      </>
    );
  }

  return (
    <div className={styles.transactions_container}>
      <header className={styles.transactions_header_container}>
        <div className={styles.header_nav}>
          <img
            src={backBtn}
            alt=""
            style={{ width: "20px", cursor: "pointer" }}
            onClick={handleGoBack}
          />
          <h4 className={styles.transaction_text}>Transaction</h4>
        </div>
        <div className={styles.filter_container}>
          <div className={styles.search_input_container}>
            <img src={searchIcon} alt="" />
            <input
              placeholder="Search by session ID"
              className={styles.input_field}
              onChange={(e) =>
                setQuery({ ...query, sessionId: e.target.value })
              }
            />
          </div>
          <Button
            className={styles.filter_modal_trigger}
            onClick={() => setShowFilter(true)}
          >
            <span>Filter by</span>
            <img src={arrowDown} alt="" />
          </Button>
        </div>
      </header>

      <div className="">
        {RenderedComponent}
        {transactionData?.data?.length > 0 && (
          <Paginator
            size={query.pageSize}
            page={query.page}
            count={transactionData?.totalCount}
            changeCurrentPage={(page: number) => setQuery({ ...query, page })}
          />
        )}
      </div>

      <TransactionsFilter
        open={showFilter}
        title="Filter Transactions"
        handleCancel={() => setShowFilter(false)}
        confirmBtnText="Apply"
        cancelBtnTxt="Cancel"
        customActionBtnTxt="Reset"
        setQuery={setQuery as Dispatch<SetStateAction<IParams>>}
        query={query}
      />
    </div>
  );
};

export default TransactionsTable;
